import React, { useState, useEffect } from "react";
import eventBus from "../../components/eventBus.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  format,
  isSameDay,
  isBefore,
  startOfDay,
  endOfDay,
  set,
  parse,
} from "date-fns";
import { enUS, pl } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import orangeSortIcon from "./../../media/orange-sort-icon.svg";
import deleteButtonIcon from "./../../media/delete-button.svg";
import { useAuth } from "./../../AuthContext.js";
import { API_URL } from "../../AuthContext";
import Logger from "../../utils/Logger";
import { useNotification } from "../../components/NotificationContext";
import { createPortal } from "react-dom";
import httpClient from "../../services/httpClient.js";
import { useTranslation } from "react-i18next";

const CarsReservations = () => {
  const { t, i18n } = useTranslation();
  // Rozdzielone stany dla dat i godzin
  const [departureDate, setDepartureDate] = useState(null);
  const [departureTime, setDepartureTime] = useState(null);
  const [arrivalDate, setArrivalDate] = useState(null);
  const [arrivalTime, setArrivalTime] = useState(null);

  // Połączone daty i godziny (dla kompatybilności)
  const [departureDateTime, setDepartureDateTime] = useState(null);
  const [arrivalDateTime, setArrivalDateTime] = useState(null);

  const [city, setCity] = useState("");
  const [tripPurpose, setTripPurpose] = useState("");
  const [selectedCar, setSelectedCar] = useState(null);
  const [errors, setErrors] = useState({});
  const [hover, setHover] = useState(false);
  const [carsReturned, setCarsReturned] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [availableCars, setAvailableCars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // const { showNotification } = useNotification();
  const [hoveredButton, setHoveredButton] = useState(false);
  const [hoveredIcon, setHoveredIcon] = useState(false);
  const [confirmationData, setConfirmationData] = useState({});
  const [cars, setCars] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [datesError, setDatesError] = useState(null);

  const { userData } = useAuth();
  const datePickerLocale = i18n.language === "en" ? enUS : pl;
  const getEmail = () => userData.userEmail;
  const getName = () => userData.userName;
  const { firstName, lastName } = getName();
  const navigate = useNavigate();

  function generateRandomCar() {
    return {
      id: Math.random().toString(36).substring(2, 15),
      make: "Toyota",
      model: Math.random().toString(36).substring(2, 7).toUpperCase(),
      licensePlate: "XYZ 1234",
    };
  }

  useEffect(() => {
    if (availableCars.length >= 0) return;
    setAvailableCars([
      ...availableCars,
      generateRandomCar(),
      generateRandomCar(),
    ]);
  }, [availableCars]);

  // Funkcja do łączenia daty i godziny
  const combineDateAndTime = (date, time) => {
    if (!date || !time) return null;

    const combined = new Date(date);
    combined.setHours(time.getHours());
    combined.setMinutes(time.getMinutes());
    combined.setSeconds(0);
    combined.setMilliseconds(0);

    return combined;
  };

  // Aktualizuj połączone daty gdy zmieniają się osobne pola
  useEffect(() => {
    const newDepartureDateTime = combineDateAndTime(
      departureDate,
      departureTime,
    );
    const newArrivalDateTime = combineDateAndTime(arrivalDate, arrivalTime);

    setDepartureDateTime(newDepartureDateTime);
    setArrivalDateTime(newArrivalDateTime);
  }, [departureDate, departureTime, arrivalDate, arrivalTime]);

  useEffect(() => {
    if (!departureDateTime || !arrivalDateTime) {
      return;
    }

    setCarsReturned(false);

    if (departureDateTime < new Date()) {
      setDatesError(t("reservations.cars.errors.departureInPast"));
      return;
    }

    if (validateDates(departureDateTime, arrivalDateTime)) {
      Logger.info("Valid dates:", {
        departureDate: departureDateTime.toISOString(),
        arrivalDate: arrivalDateTime.toISOString(),
      });
      setDatesError(null);
      handleSubmit();
    } else {
      setDatesError(t("reservations.cars.errors.invalidDateRange"));
    }
  }, [departureDateTime, arrivalDateTime, t]);

  const validateDates = (departure, arrival) => {
    if (!departure || !arrival) return false;
    return arrival.getTime() > departure.getTime();
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setCarsReturned(false);

    try {
      const startDate = encodeURIComponent(
        format(departureDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
      );
      const endDate = encodeURIComponent(
        format(arrivalDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
      );
      const response = await httpClient.get(
        `cars/available-cars?startdatelocal=${startDate}&enddatelocal=${endDate}`,
        { withAuth: true },
      );
      setAvailableCars(response.data || []);
    } catch (error) {
      Logger.error("Błąd podczas pobierania samochodów:", error);
      setError(t("reservations.cars.fetchAvailableCarsError"));
    } finally {
      setLoading(false);
      setCarsReturned(true);
    }
  };

  const handleCarSelection = (car) => {
    // Don't allow selection if car is not available
    if (car.isAvailable === false) {
      return;
    }
    setSelectedCar(selectedCar === car ? null : car);
  };

  // Handlery dla osobnych pól daty i godziny
  const handleDepartureDateChange = (date) => {
    setDepartureDate(date);
  };

  const handleDepartureTimeChange = (time) => {
    setDepartureTime(time);
  };

  const handleArrivalDateChange = (date) => {
    setArrivalDate(date);
  };

  const handleArrivalTimeChange = (time) => {
    setArrivalTime(time);
  };

  const decideMinTimeForDeparture = () => {
    if (!departureDate) return new Date();
    if (isSameDay(departureDate, new Date())) {
      return new Date();
    }
    return startOfDay(departureDate);
  };

  const decideMinTimeForArrival = () => {
    if (!arrivalDate) return new Date();
    if (isSameDay(arrivalDate, new Date())) {
      return new Date();
    }
    return startOfDay(arrivalDate);
  };

  const sendReservation = async (reservationData) => {
    try {
      const response = await httpClient.post(
        "/CarReservations",
        reservationData,
        { withAuth: true },
      );
      // showNotification("Rezerwacja została dodana pomyślnie!", "success");
      setShowConfirmation(false);
      eventBus.emit("car-reserved", {});
      navigate("/rezerwacje/samochody/");
    } catch (error) {
      Logger.error("Błąd podczas wysyłania rezerwacji:", error);
      // showNotification("Nie udało się wysłać rezerwacji.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReservation = () => {
    const newErrors = {};
    if (!departureDate)
      newErrors.departureDate = t(
        "reservations.cars.errors.selectDepartureDate",
      );
    if (!departureTime)
      newErrors.departureTime = t(
        "reservations.cars.errors.selectDepartureTime",
      );
    if (!arrivalDate)
      newErrors.arrivalDate = t("reservations.cars.errors.selectArrivalDate");
    if (!arrivalTime)
      newErrors.arrivalTime = t("reservations.cars.errors.selectArrivalTime");
    if (departureDateTime && arrivalDateTime) {
      if (arrivalDateTime <= departureDateTime) {
        newErrors.arrivalTime = t("reservations.cars.errors.invalidDateRange");
      }
    }
    if (!city) newErrors.city = t("reservations.cars.errors.enterCity");
    if (!tripPurpose)
      newErrors.tripPurpose = t("reservations.cars.errors.enterPurpose");
    if (!selectedCar)
      newErrors.selectedCar = t("reservations.cars.errors.selectCar");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      setConfirmationData({
        car: `${selectedCar.make} ${selectedCar.model}`,
        departureDate: departureDateTime
          ? format(departureDateTime, "dd-MM-yyyy HH:mm")
          : "",
        arrivalDate: arrivalDateTime
          ? format(arrivalDateTime, "dd-MM-yyyy HH:mm")
          : "",
        city,
        tripPurpose,
      });
      setShowConfirmation(true);
    }
  };

  const handleConfirm = () => {
    setIsLoading(true);
    const reservation = {
      carId: selectedCar.id,
      startDateLocal: format(departureDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
      endDateLocal: format(arrivalDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
      location: city,
      reason: tripPurpose,
    };
    sendReservation(reservation);
  };

  const handleCancel = () => setShowConfirmation(false);
  const handleCancelChange = () => navigate("/rezerwacje/samochody/");
  const handleIconClick = () => navigate("/rezerwacje/samochody");

  const renderConfirmationModal = () => {
    if (!showConfirmation) return null;

    return createPortal(
      <div className="confirmation-overlay">
        <div className="confirmation-box">
          <img
            className="close-button"
            src={deleteButtonIcon}
            alt={t("common.close")}
            onClick={handleCancel}
          />
          <p>{t("reservations.cars.confirmCreate")}</p>
          <div className="confirmation-details">
            <p>
              <strong>{t("reservations.cars.car")} : </strong>{" "}
              {confirmationData.car}
            </p>
            <p>
              <strong>{t("reservations.cars.departureDateTime")} : </strong>{" "}
              {confirmationData.departureDate}
            </p>
            <p>
              <strong>{t("reservations.cars.arrivalDateTime")} : </strong>{" "}
              {confirmationData.arrivalDate}
            </p>
            <p>
              <strong>{t("reservations.cars.city")} : </strong>{" "}
              {confirmationData.city}
            </p>
            <p>
              <strong>{t("reservations.cars.tripPurpose")} : </strong>{" "}
              {confirmationData.tripPurpose}
            </p>
          </div>
          <div className="confirmation-buttons">
            {isLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>{t("reservations.cars.creatingReservation")}</p>
              </div>
            ) : (
              <>
                <button onClick={handleConfirm}>{t("common.yes")}</button>
                <button onClick={handleCancelChange}>{t("common.no")}</button>
              </>
            )}
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  return (
    <div className="cars-reservations-container">
      <div className="main-site" style={{ justifyContent: "space-between" }}>
        <h1>{t("reservations.cars.businessTripsTitle")}</h1>
        <div
          className="orange-sort-icon"
          onClick={handleIconClick}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            backgroundImage: `url(${orangeSortIcon})`,
            transform: hover ? "rotate(180deg)" : "rotate(90deg)",
            cursor: "pointer",
          }}
        />
      </div>

      <div className="date-inputs">
        <div className="the-first-line">
          <div className="input-group">
            <div className="date-time-container">
              <div className="date-picker-container">
                <label htmlFor="departureDate">
                  {t("reservations.cars.departureDate")}
                </label>
                <DatePicker
                  selected={departureDate}
                  onChange={handleDepartureDateChange}
                  dateFormat="dd-MM-yyyy"
                  locale={datePickerLocale}
                  placeholderText={t("reservations.cars.selectDepartureDate")}
                  minDate={new Date()}
                />
                {errors.departureDate && (
                  <span className="error-message">{errors.departureDate}</span>
                )}
              </div>

              <div className="time-picker-container">
                <label htmlFor="departureTime">
                  {t("reservations.cars.departureTime")}
                </label>
                <DatePicker
                  selected={departureTime}
                  onChange={handleDepartureTimeChange}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption={t("reservations.common.time")}
                  dateFormat="HH:mm"
                  locale={datePickerLocale}
                  placeholderText={t("reservations.cars.selectDepartureTime")}
                  minTime={decideMinTimeForDeparture()}
                  maxTime={new Date(0, 0, 0, 23, 59, 59)}
                />
                {errors.departureTime && (
                  <span className="error-message">{errors.departureTime}</span>
                )}
              </div>

              <div className="date-picker-container">
                <label htmlFor="arrivalDate">
                  {t("reservations.cars.arrivalDate")}
                </label>
                <DatePicker
                  selected={arrivalDate}
                  onChange={handleArrivalDateChange}
                  dateFormat="dd-MM-yyyy"
                  disabled={!departureDate || !departureTime}
                  locale={datePickerLocale}
                  placeholderText={t("reservations.cars.selectArrivalDate")}
                  minDate={departureDate || new Date()}
                />
                {errors.arrivalDate && (
                  <span className="error-message">{errors.arrivalDate}</span>
                )}
              </div>

              <div className="time-picker-container">
                <label htmlFor="arrivalTime">
                  {t("reservations.cars.arrivalTime")}
                </label>
                <DatePicker
                  selected={arrivalTime}
                  onChange={handleArrivalTimeChange}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption={t("reservations.common.time")}
                  dateFormat="HH:mm"
                  locale={datePickerLocale}
                  placeholderText={t("reservations.cars.selectArrivalTime")}
                  disabled={!arrivalDate || !departureDate || !departureTime}
                  minTime={decideMinTimeForArrival()}
                  maxTime={new Date(0, 0, 0, 23, 59, 59)}
                />
                {errors.arrivalTime && (
                  <span className="error-message">{errors.arrivalTime}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {datesError && <div className="error-message">{datesError}</div>}

      {carsReturned && (
        <div className="car-reservation-details">
          <div className="available-group">
            <label>{t("reservations.cars.availableCars")}</label>
            {loading && <p>{t("reservations.cars.loadingAvailableCars")}</p>}
            {error && <p className="error-message">{error}</p>}

            {!loading && availableCars.length === 0 && !error && (
              <div className="no-absences">
                {t("reservations.cars.noCarsForPeriod")}
              </div>
            )}
            <div className="cars-list" style={{ marginTop: "2rem" }}>
              {availableCars.map((car) => {
                const isDisabled = car.isAvailable === false;
                return (
                  <div
                    key={`${car.make} ${car.model}`}
                    className={`car-item ${isDisabled ? "disabled" : ""}`}
                    onClick={() => handleCarSelection(car)}
                    style={{
                      opacity: isDisabled ? 0.5 : 1,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      pointerEvents: isDisabled ? "none" : "auto",
                    }}
                  >
                    <span
                      className={`car-checkbox ${
                        selectedCar === car ? "checked" : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCarSelection(car);
                      }}
                    />
                    {car.make} {car.model} - {car.licensePlate}
                  </div>
                );
              })}
            </div>
            {errors.selectedCar && (
              <span className="error-message">{errors.selectedCar}</span>
            )}
          </div>
          <div className="car-reservation-details-input">
            <label htmlFor="city">{t("reservations.cars.city")}</label>
            <input
              type="text"
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder={t("reservations.cars.enterCity")}
            />
            {errors.city && (
              <span className="error-down-message">{errors.city}</span>
            )}
          </div>

          <div className="car-reservation-details-input">
            <label htmlFor="tripPurpose">
              {t("reservations.cars.tripPurpose")}
            </label>
            <textarea
              id="tripPurpose"
              value={tripPurpose}
              onChange={(e) => setTripPurpose(e.target.value)}
              placeholder={t("reservations.cars.enterPurpose")}
            />
            {errors.tripPurpose && (
              <span className="error-down-message">{errors.tripPurpose}</span>
            )}
            <button className="reservations-button" onClick={handleReservation}>
              {t("reservations.cars.makeReservation")}
            </button>
          </div>
        </div>
      )}

      {renderConfirmationModal()}
    </div>
  );
};

export default CarsReservations;
